import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import https from "https";
import axios from "axios";
import log from "../utils/log";
import metaData from "../data/metaData";
import c from "../utils/common";

//
import useAuthStore from "../states/useAuth";
import useRequestStore from "../states/useRequest";
import useDB from "../hooks/useDB";

const useRequest = () => {
  const { auth, updateUser } = useAuthStore();
  const { resetRequest } = useRequestStore();
  const {
    getSingleRequest: getSingleRequestFromCache,
    getRequests: getRequestsFromCache,
    updateSingleRequestCache,
    updateRequestsCache,
  } = useRequestStore();

  const { get } = useDB();

  /**
   * getSingleShopRequest:
   * This function fetches a single request by its ID, first checking if the request's data is cached.
   *
   * - If the request data is found in the cache, it returns the cached data.
   * - If the request data is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary object), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {number|string} id - The unique ID of the request to fetch.
   * @param {boolean} saveCache - Whether save response to cache or not, default is true
   * @returns {Object} - The request data, either from the cache or fetched via API.
   */
  const getSingleShopRequest = async (id, saveCache = true, useCache = true) => {
    if (!id) {
      return undefined;
    }

    let cache = useCache ? getSingleRequestFromCache(id) : null;

    if (!cache) {
      log.debug("no cache, axios");
      let response = await get("request/shop/query", {
        id,
        populate: [
          {
            path: "company",
            select: "name br address",
          },
          {
            path: "brand",
            select: "name logo description category",
          },
          {
            path: "shop",
          },
          {
            path: "initialRequest",
            select: "info.company info.brand",
          },
          {
            path: "user",
            select: "email companyId",
            populate: {
              path: "company",
              select: "permission",
            },
          },
          {
            path: "approver",
            select: "email",
          },
        ],
      });

      // Cache the fetched request data
      if (saveCache) {
        updateSingleRequestCache("update", [response]);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  const getSingleProductRequest = async (id, saveCache = true, useCache = true) => {
    if (!id) {
      return undefined;
    }

    let cache = useCache ? getSingleRequestFromCache(id) : null;

    if (!cache) {
      log.debug("no cache, axios");
      let response = await get("request/product/query", {
        id,
        populate: [
          {
            path: "user",
            select: "email companyId",
          },
          {
            path: "approver",
            select: "email",
          },
          {
            path: "info.product.redeemShops",
            select: "name logo address companyId brandId",
            populate: [
              {
                path: "company",
                select: "name",
              },
              {
                path: "brand",
                select: "name logo",
              },
            ],
          },
        ],
      });

      // Cache the fetched request data
      if (saveCache) {
        updateSingleRequestCache("update", [response]);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  /**
   * getShopRequests:
   * This function fetches a list of requests based on a query (such as pagination or filters),
   * first checking if the query result is cached.
   *
   * - If the query result is found in the cache, it returns the cached data.
   * - If the query result is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary array), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {string} query - The query string to filter or paginate the requests (e.g., "?status=0&pagination=1").
   * @param {boolean} saveCache - Whether save response to cache or not, default is true
   * @returns {Array} - The list of requests, either from the cache or fetched via API.
   */
  const getShopRequests = async (query, saveCache = true, useCache = true) => {
    let cache = useCache ? getRequestsFromCache(query) : null;

    if (!cache) {
      log.debug("no cache, axios");
      let response = await get("request/shops/query", {
        select: "-info.brand.category -info.company.address -info.company.br -info.shop.address",
        populate: [
          {
            path: "company",
            select: "name",
          },
          {
            path: "brand",
            select: "name logo",
          },
          {
            path: "shop",
            select: "name logo",
          },
          {
            path: "initialRequest",
            select: "info.company info.brand",
          },
        ],
      });

      // Cache the fetched requests data
      if (saveCache) {
        updateRequestsCache("update", query, response);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  const getProductRequests = async (query, saveCache = true, useCache = true) => {
    let cache = useCache ? getRequestsFromCache(query) : null;

    if (!cache) {
      log.debug("no cache, axios");
      let response = await get("request/products/query", {
        // select: "-info.brand.category -info.company.address -info.company.br -info.shop.address",
        populate: [
          {
            path: "company",
            select: "name",
          },
          {
            path: "brand",
            select: "name logo",
          },
        ],
      });

      // Cache the fetched requests data
      if (saveCache) {
        updateRequestsCache("update", query, response);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  const createCompany = async ({ company, brand, shop }) => {
    let data = {
      company,
      brand,
      shop,
    };

    log.debug(data);

    let res = await axios.post(metaData.serverURL + "shop", data, {
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    log.debug(res);

    updateUser("update", { newUserSetting: { getStarted: true } });
    resetRequest();
    return res.data;
  };

  const createShop = async ({ shop }) => {
    // log.debug({ shop });
    let data = { shop };

    let res = await axios.post(metaData.serverURL + "shop", data, {
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(res);

    // updateSingleRequestCache("update", [res.data]); // Cache the fetched request data
    return res.data;
  };

  const updateShopRequest = async (requestId, requestData, saveCache = true) => {
    const response = await axios.patch(metaData.serverURL + "request/shop", requestData, {
      params: { id: requestId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);

    // Cache the fetched request data
    if (saveCache) {
      updateSingleRequestCache("update", [response.data]);
    }
    return response.data; // Return the updated data
  };

  const createProduct = async ({ product }) => {
    // log.debug({ product });
    let data = { product };

    let res = await axios.post(metaData.serverURL + "product", data, {
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(res);

    // updateSingleRequestCache("update", [res.data]); // Cache the fetched request data
    return res.data;
  };

  const updateProductRequest = async (requestId, requestData, saveCache = true) => {
    const response = await axios.patch(metaData.serverURL + "request/product", requestData, {
      params: { id: requestId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);

    // Cache the fetched request data
    if (saveCache) {
      updateSingleRequestCache("update", [response.data]);
    }
    return response.data; // Return the updated data
  };

  return {
    getSingleShopRequest,
    getSingleProductRequest,
    getShopRequests,
    getProductRequests,
    createCompany,
    createShop,
    updateShopRequest,
    createProduct,
    updateProductRequest,
  };
};

export default useRequest;
